import { type ReactNode } from "react";
import { IconCheck, IconChevronLeft } from "@tabler/icons-react";
import { cn } from "../../../../lib/utils";

/**
 * The right-hand column of the settings modal: the page's sections stacked, and
 * the way back when the page opened from a row of another one (Account ›
 * Delete account, Partner sharing › See our partners).
 */
export function SettingsPage({
  back,
  children,
}: {
  back: { label: string; onBack: () => void } | null;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-5 overflow-y-auto pt-7 pr-10 pb-8 pl-8">
      {back !== null ? (
        <button
          type="button"
          onClick={back.onBack}
          className="-ml-1 flex cursor-pointer items-center gap-1 self-start border-none bg-transparent p-0 text-sm font-medium text-ds-slate-500 hover:text-ds-slate-950 [&_svg]:size-4"
        >
          <IconChevronLeft stroke={1.5} />
          {back.label}
        </button>
      ) : null}
      {children}
    </div>
  );
}

/** One titled block of a page: the title, an optional line, then a hairline. */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string | null;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="text-heading-xs font-semibold text-ds-slate-950">
        {title}
      </h2>
      {description !== null ? (
        <p className="m-0 text-sm text-ds-slate-700">{description}</p>
      ) : null}
      <div className="h-px bg-ds-slate-100" />
      <div className="flex flex-col gap-1">{children}</div>
    </section>
  );
}

/** A setting: what it is on the left, the control that changes it on the right. */
export function SettingsRow({
  title,
  subtitle,
  leading,
  right,
}: {
  title: string;
  subtitle: string | null;
  /** An avatar or an icon before the labels, or `null`. */
  leading: ReactNode | null;
  right: ReactNode | null;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4 py-2.5">
      {leading}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-ds-slate-950">{title}</span>
        {subtitle !== null ? (
          <span className="text-desktop-meta text-ds-slate-700">
            {subtitle}
          </span>
        ) : null}
      </span>
      {right}
    </div>
  );
}

/**
 * One choice of a list page (Figma "option · …" of My main specialty and
 * Language): the labels, and a check on the selected one.
 */
export function SettingsOptionRow({
  lines,
  selected,
  disabled,
  onSelect,
  children,
}: {
  /** One label (a specialty) or a name over a second line (a language). */
  lines: 1 | 2;
  selected: boolean;
  /** While a pick is being saved, so a second one cannot race it. */
  disabled: boolean;
  onSelect: () => void;
  /** The labels, stacked. */
  children: ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 rounded-lg border-none px-3 text-left disabled:cursor-not-allowed",
        lines === 1 ? "py-2.5" : "py-2",
        selected ? "bg-ds-slate-100" : "bg-transparent hover:bg-ds-slate-100/60"
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</span>
      {selected ? (
        <IconCheck stroke={1.5} className="size-4 shrink-0 text-ds-slate-950" />
      ) : null}
    </button>
  );
}
